import { useEffect, useMemo, useRef, useState } from 'react';
import type { ExtractedEvent } from '../types';
import {
  type DatePreset,
  type FilterState,
  type SortKey,
  countActiveFilters,
  cycleIncludeExclude,
  DEFAULT_FILTERS,
  uniqSorted,
} from '../lib/eventFilters';
import DistanceControl from './DistanceControl';
import {
  type SavedView,
  deleteView,
  deserializeFilters,
  loadSavedViews,
  saveView,
} from '../lib/savedViews';
import { groupTags, formatTag, sortByLabel, tagIcon } from '../lib/tagTaxonomy';
import { EVENT_TOPICS, TIME_OF_DAY_OPTIONS, eventTopicIds, topicIcon, topicLabel } from '../lib/eventTopics';
import { formatCategory } from '../lib/venue-categories';
import { confirmDialog, promptDialog } from '../lib/dialogs';
import { eventTodayKey } from '../lib/eventDates';

interface Props {
  events: ExtractedEvent[] | null;
  filters: FilterState;
  setFilters: (updater: (f: FilterState) => FilterState) => void;
}

/** Tag groups offered as dropdowns behind "More", in display order. Time of day follows Setting. */
const MORE_TAG_GROUP_IDS = ['genre', 'audience', 'setting', 'price', 'format'];

/**
 * Compact, modern filter bar.
 *
 * Layout:
 *   Row 1 (toolbar): Search · When · Sort · Image · Tickets · More-filters · Reset
 *   Row 2: Topics chip row (always visible — the primary discovery filter)
 *   Row 3: Filters from old links / saved views the bar no longer offers, only when active
 *   Collapsed panel: Music genre, Audience, Setting, Time of day, Price, Format,
 *   Neighborhood, Source, Venue
 */
export default function EventFiltersBar({ events, filters, setFilters }: Props) {
  const [showMore, setShowMore] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews());

  const presentTopics = useMemo(() => {
    const present = new Set<string>();
    for (const e of events ?? []) for (const t of eventTopicIds(e)) present.add(t);
    return present;
  }, [events]);
  const allNeighborhoods = useMemo(
    () => sortByLabel(uniqSorted(events?.map((e) => e.neighborhood) ?? [])),
    [events]
  );
  const allVenues = useMemo(
    () =>
      Array.from(
        new Map(
          (events ?? [])
            .filter((e) => e.venueId && e.venueName)
            .map((e) => [e.venueId!, { id: e.venueId!, name: e.venueName! }])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [events]
  );
  const allSources = useMemo(
    () => sortByLabel(uniqSorted(events?.map((e) => e.sourceType) ?? [])),
    [events]
  );
  const allTags = useMemo(
    () => uniqSorted((events ?? []).flatMap((e) => e.tags ?? [])),
    [events]
  );

  // Only surface the "Show hidden" toggle when at least one loaded event has
  // a venue-defined excluded-tag list (or the flag is already on via a shared
  // URL). Avoids cluttering the toolbar with a control that does nothing.
  const hasHideableEvents = useMemo(
    () => (events ?? []).some((e) => (e.venueExcludedTags?.length ?? 0) > 0),
    [events],
  );
  const showShowHiddenToggle = hasHideableEvents || filters.showExcludedTags;

  const filterCount = countActiveFilters(filters);
  // Preserve the user's saved origin/distance + active preset/sort across "Clear"
  // \u2014 those feel like view settings, not filter chips. Distance is removed by
  // the dedicated \u00d7 button on the address pill.
  const reset = () =>
    setFilters((prev) => ({
      ...DEFAULT_FILTERS,
      preset: prev.preset,
      customFrom: prev.customFrom,
      customTo: prev.customTo,
      sort: prev.sort,
      origin: prev.origin,
      maxDistanceMiles: prev.maxDistanceMiles,
      // "Show hidden" is a view setting (like sort), not a filter chip
      // — we don't want "Clear filters" to silently re-hide events the
      // user just chose to reveal.
      showExcludedTags: prev.showExcludedTags,
    }));

  const handleSaveView = async () => {
    const name = await promptDialog({
      title: 'Save view',
      label: 'Name this view',
      placeholder: 'e.g. Music this weekend',
    });
    if (!name) return;
    setSavedViews(saveView(name, filters));
  };

  const handleApplyView = (view: SavedView) => {
    const next = deserializeFilters(view.filters);
    setFilters(() => next);
  };

  const handleDeleteView = async (view: SavedView) => {
    if (!(await confirmDialog({
      title: 'Delete saved view',
      body: `Delete saved view “${view.name}”?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setSavedViews(deleteView(view.id));
  };

  const tagGroups = groupTags(allTags);
  const moreTagGroups = MORE_TAG_GROUP_IDS.flatMap((id) => tagGroups.filter((g) => g.id === id));
  const topicIds = EVENT_TOPICS.map((t) => t.id).filter(
    (id) => presentTopics.has(id) || filters.topics.has(id) || filters.topicsExcluded.has(id),
  );

  // Tags and venue categories set by an old link or saved view that the bar
  // no longer offers. Shown as removable pills so they never filter silently.
  const offeredTags = new Set(moreTagGroups.flatMap((g) => g.tags));
  const legacyPills = [
    ...[...filters.categories].map((value) => ({ kind: 'cat' as const, value, ex: false })),
    ...[...filters.categoriesExcluded].map((value) => ({ kind: 'cat' as const, value, ex: true })),
    ...[...filters.tags].filter((t) => !offeredTags.has(t)).map((value) => ({ kind: 'tag' as const, value, ex: false })),
    ...[...filters.tagsExcluded].filter((t) => !offeredTags.has(t)).map((value) => ({ kind: 'tag' as const, value, ex: true })),
  ];
  const removeLegacy = (kind: 'cat' | 'tag', value: string) =>
    setFilters((f) => {
      const inc = new Set(kind === 'cat' ? f.categories : f.tags);
      const exc = new Set(kind === 'cat' ? f.categoriesExcluded : f.tagsExcluded);
      inc.delete(value);
      exc.delete(value);
      return kind === 'cat'
        ? { ...f, categories: inc, categoriesExcluded: exc }
        : { ...f, tags: inc, tagsExcluded: exc };
    });

  // Count active filters that live behind "More filters" so the badge is meaningful.
  const moreActiveCount =
    filters.times.size +
    filters.timesExcluded.size +
    moreTagGroups.reduce(
      (n, g) =>
        n +
        g.tags.filter((t) => filters.tags.has(t) || filters.tagsExcluded.has(t)).length,
      0,
    ) +
    filters.neighborhoods.size +
    filters.neighborhoodsExcluded.size +
    filters.sources.size +
    filters.sourcesExcluded.size +
    filters.venues.size +
    filters.venuesExcluded.size;

  const timeOfDay = (
    <MultiSelect
      key="time-of-day"
      label="Time of day"
      options={TIME_OF_DAY_OPTIONS}
      selected={filters.times}
      excluded={filters.timesExcluded}
      onCycle={(t) =>
        setFilters((f) => {
          const r = cycleIncludeExclude(f.times, f.timesExcluded, t);
          return { ...f, times: r.active, timesExcluded: r.excluded };
        })
      }
      onBulkInclude={(values) =>
        setFilters((f) => {
          const nextSel = new Set(f.times);
          const nextEx = new Set(f.timesExcluded);
          for (const v of values) {
            nextSel.add(v);
            nextEx.delete(v);
          }
          return { ...f, times: nextSel, timesExcluded: nextEx };
        })
      }
      onBulkClear={(values) =>
        setFilters((f) => {
          const nextSel = new Set(f.times);
          const nextEx = new Set(f.timesExcluded);
          for (const v of values) {
            nextSel.delete(v);
            nextEx.delete(v);
          }
          return { ...f, times: nextSel, timesExcluded: nextEx };
        })
      }
    />
  );

  return (
    <section aria-label="Filters" className="mh-filters mb-3">
      {/* Toolbar */}
      <div className="mh-filters__toolbar">
        <div className="mh-filters__search">
          <i className="bi bi-search" aria-hidden="true" />
          <input
            id="event-search"
            type="search"
            className="form-control form-control-sm"
            placeholder="Search title, description, venue…"
            aria-label="Search events"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
        </div>

        <select
          id="event-when"
          className="form-select form-select-sm mh-filters__select"
          aria-label="Date range"
          value={filters.preset}
          onChange={(e) => {
            const preset = e.target.value as DatePreset;
            setFilters((f) => {
              if (preset !== 'custom') return { ...f, preset };
              const today = eventTodayKey();
              return {
                ...f, preset,
                customFrom: f.customFrom ?? today,
                customTo: f.customTo ?? f.customFrom ?? today,
              };
            });
          }}
        >
          <optgroup label="Upcoming">
            <option value="today">Today</option>
            <option value="weekend">This weekend</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="60d">Next 60 days</option>
            <option value="all">All upcoming</option>
          </optgroup>
          <optgroup label="Past">
            <option value="past-week">Past week</option>
            <option value="past-month">Past month</option>
            <option value="past-90d">Past 90 days</option>
            <option value="all-past">All past events</option>
          </optgroup>
          <optgroup label="Combined">
            <option value="all-time">All time</option>
          </optgroup>
          <optgroup label="Custom">
            <option value="custom">Custom range…</option>
          </optgroup>
        </select>

        {filters.preset === 'custom' && (
          <CustomDateRange filters={filters} setFilters={setFilters} />
        )}

        <select
          id="event-sort"
          className="form-select form-select-sm mh-filters__select"
          aria-label="Sort by"
          value={filters.sort}
          onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value as SortKey }))}
        >
          <option value="date-asc">Soonest</option>
          <option value="date-desc">Latest</option>
          <option value="title-asc">Title A–Z</option>
          <option value="venue-asc">Venue A–Z</option>
          <option value="recently-added">Recently added</option>
          <option value="distance-asc" disabled={!filters.origin}>
            Nearest{filters.origin ? '' : ' (set address)'}
          </option>
        </select>

        <select
          id="event-recurring-mode"
          className="form-select form-select-sm mh-filters__select"
          aria-label="Recurring events"
          value={filters.recurringMode}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              recurringMode: e.target.value as FilterState['recurringMode'],
            }))
          }
          title="Control how weekly/recurring events appear"
        >
          <option value="show-all">Show all repeats</option>
          <option value="hide-instances">Hide repeats</option>
          <option value="only-repeats">Only repeat events</option>
          <option value="hide-weekly">Hide weekly events</option>
        </select>

        <select
          id="event-venue-visibility"
          className="form-select form-select-sm mh-filters__select"
          aria-label="Venue visibility"
          value={filters.venueVisibility}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              venueVisibility: e.target.value as FilterState['venueVisibility'],
            }))
          }
          title="Filter events by the moderation status of their venue. Default shows events from active venues plus venues an admin dismissed (events kept). 'Active venues only' is stricter; 'Show all' also reveals events from declined / removed venues."
        >
          <option value="active-only">Active venues only</option>
          <option value="with-dismissed">Active &amp; dismissed venues</option>
          <option value="all">Show all (incl. hidden)</option>
        </select>

        <DistanceControl
          origin={filters.origin}
          maxDistanceMiles={filters.maxDistanceMiles}
          onChange={(next) =>
            setFilters((f) => ({
              ...f,
              origin: next.origin,
              maxDistanceMiles: next.maxDistanceMiles,
              // Drop the "distance-asc" sort if the origin is removed—otherwise the
              // sort would silently fall through to the default in applyFilters.
              sort: !next.origin && f.sort === 'distance-asc' ? 'date-asc' : f.sort,
            }))
          }
          idPrefix="mh-events-distance"
        />

        <div className="mh-filters__toggles" role="group" aria-label="Quick filters">
          <button
            type="button"
            className={`mh-toggle ${filters.hasImage ? 'mh-toggle--on' : ''}`}
            aria-pressed={filters.hasImage}
            onClick={() => setFilters((f) => ({ ...f, hasImage: !f.hasImage }))}
            title="Only events with an image"
          >
            <i className="bi bi-image" aria-hidden="true" /> Image
          </button>
          <button
            type="button"
            className={`mh-toggle ${filters.hasTicket ? 'mh-toggle--on' : ''}`}
            aria-pressed={filters.hasTicket}
            onClick={() => setFilters((f) => ({ ...f, hasTicket: !f.hasTicket }))}
            title="Only events with tickets or a link"
          >
            <i className="bi bi-ticket-perforated" aria-hidden="true" /> Tickets
          </button>
          {showShowHiddenToggle && (
            <button
              type="button"
              className={`mh-toggle ${filters.showExcludedTags ? 'mh-toggle--on' : ''}`}
              aria-pressed={filters.showExcludedTags}
              onClick={() => setFilters((f) => ({ ...f, showExcludedTags: !f.showExcludedTags }))}
              title="Reveal events that venues hide by default (e.g. happy hour, brunch)"
            >
              <i
                className={`bi ${filters.showExcludedTags ? 'bi-eye' : 'bi-eye-slash'}`}
                aria-hidden="true"
              />{' '}
              {filters.showExcludedTags ? 'Hidden shown' : 'Show hidden'}
            </button>
          )}
          <button
            type="button"
            className={`mh-toggle ${showMore ? 'mh-toggle--on' : ''}`}
            aria-expanded={showMore}
            aria-controls="mh-more-filters"
            onClick={() => setShowMore((v) => !v)}
            title="Show more filters"
          >
            <i className={`bi bi-${showMore ? 'sliders' : 'sliders'}`} aria-hidden="true" /> More
            {moreActiveCount > 0 && (
              <span className="mh-toggle__badge" aria-label={`${moreActiveCount} active`}>
                {moreActiveCount}
              </span>
            )}
            <i
              className={`bi ${showMore ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`}
              aria-hidden="true"
            />
          </button>
        </div>

        {filterCount > 0 && (
          <button type="button" className="btn btn-link btn-sm mh-filters__reset" onClick={reset}>
            <i className="bi bi-x-circle me-1" aria-hidden="true" />
            Clear {filterCount}
          </button>
        )}

        <SavedViewsMenu
          views={savedViews}
          onApply={handleApplyView}
          onSave={handleSaveView}
          onDelete={handleDeleteView}
          hasActiveFilters={filterCount > 0 || filters.preset !== '60d'}
        />
      </div>

      {/* Always-visible chip row: Topics */}
      <div className="mh-filters__groups">
        {topicIds.length > 0 && (
          <ChipRow
            label="Topics"
            items={topicIds}
            active={filters.topics}
            excluded={filters.topicsExcluded}
            labelOf={topicLabel}
            iconOf={topicIcon}
            onCycle={(t) =>
              setFilters((f) => {
                const r = cycleIncludeExclude(f.topics, f.topicsExcluded, t);
                return { ...f, topics: r.active, topicsExcluded: r.excluded };
              })
            }
          />
        )}
        {legacyPills.length > 0 && (
          <fieldset className="mh-filter-row">
            <legend className="mh-filter-row__label">Also filtering</legend>
            <div className="mh-filter-row__chips">
              {legacyPills.map((p) => (
                <button
                  key={`${p.kind}:${p.ex ? 'x' : 'i'}:${p.value}`}
                  type="button"
                  className={`mh-chip-btn ${p.ex ? 'mh-chip-btn--ex' : 'mh-chip-btn--on'}`}
                  title="Click to remove this filter"
                  onClick={() => removeLegacy(p.kind, p.value)}
                >
                  {p.ex ? 'Not ' : ''}
                  {p.kind === 'cat' ? formatCategory(p.value) : formatTag(p.value)}
                  <span className="ms-1" aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          </fieldset>
        )}
      </div>

      {/* Collapsed panel */}
      {showMore && (
        <div id="mh-more-filters" className="mh-filters__more">
          <div className="mh-filters__more-grid">
            {moreTagGroups.map((group) => [
              <MultiSelect
                key={group.id}
                label={group.label}
                options={group.tags.map((t) => ({ value: t, label: formatTag(t), icon: tagIcon(t) }))}
                selected={filters.tags}
                excluded={filters.tagsExcluded}
                onCycle={(t) =>
                  setFilters((f) => {
                    const r = cycleIncludeExclude(f.tags, f.tagsExcluded, t);
                    return { ...f, tags: r.active, tagsExcluded: r.excluded };
                  })
                }
                onBulkInclude={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.tags);
                    const nextEx = new Set(f.tagsExcluded);
                    for (const v of values) {
                      nextSel.add(v);
                      nextEx.delete(v);
                    }
                    return { ...f, tags: nextSel, tagsExcluded: nextEx };
                  })
                }
                onBulkClear={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.tags);
                    const nextEx = new Set(f.tagsExcluded);
                    for (const v of values) {
                      nextSel.delete(v);
                      nextEx.delete(v);
                    }
                    return { ...f, tags: nextSel, tagsExcluded: nextEx };
                  })
                }
              />,
              group.id === 'setting' && timeOfDay,
            ])}
            {!moreTagGroups.some((g) => g.id === 'setting') && timeOfDay}
            {allNeighborhoods.length > 0 && (
              <MultiSelect
                label="Neighborhood"
                options={allNeighborhoods.map((n) => ({ value: n, label: formatTag(n) }))}
                selected={filters.neighborhoods}
                excluded={filters.neighborhoodsExcluded}
                onCycle={(v) =>
                  setFilters((f) => {
                    const r = cycleIncludeExclude(f.neighborhoods, f.neighborhoodsExcluded, v);
                    return { ...f, neighborhoods: r.active, neighborhoodsExcluded: r.excluded };
                  })
                }
                onBulkInclude={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.neighborhoods);
                    const nextEx = new Set(f.neighborhoodsExcluded);
                    for (const v of values) {
                      nextSel.add(v);
                      nextEx.delete(v);
                    }
                    return { ...f, neighborhoods: nextSel, neighborhoodsExcluded: nextEx };
                  })
                }
                onBulkClear={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.neighborhoods);
                    const nextEx = new Set(f.neighborhoodsExcluded);
                    for (const v of values) {
                      nextSel.delete(v);
                      nextEx.delete(v);
                    }
                    return { ...f, neighborhoods: nextSel, neighborhoodsExcluded: nextEx };
                  })
                }
              />
            )}
            {allSources.length > 0 && (
              <MultiSelect
                label="Source"
                options={allSources.map((s) => ({ value: s, label: formatTag(s) }))}
                selected={filters.sources}
                excluded={filters.sourcesExcluded}
                onCycle={(v) =>
                  setFilters((f) => {
                    const r = cycleIncludeExclude(f.sources, f.sourcesExcluded, v);
                    return { ...f, sources: r.active, sourcesExcluded: r.excluded };
                  })
                }
                onBulkInclude={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.sources);
                    const nextEx = new Set(f.sourcesExcluded);
                    for (const v of values) {
                      nextSel.add(v);
                      nextEx.delete(v);
                    }
                    return { ...f, sources: nextSel, sourcesExcluded: nextEx };
                  })
                }
                onBulkClear={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.sources);
                    const nextEx = new Set(f.sourcesExcluded);
                    for (const v of values) {
                      nextSel.delete(v);
                      nextEx.delete(v);
                    }
                    return { ...f, sources: nextSel, sourcesExcluded: nextEx };
                  })
                }
              />
            )}
            {allVenues.length > 0 && (
              <MultiSelect
                label="Venue"
                options={allVenues.map((v) => ({ value: v.id, label: v.name }))}
                selected={filters.venues}
                excluded={filters.venuesExcluded}
                onCycle={(v) =>
                  setFilters((f) => {
                    const r = cycleIncludeExclude(f.venues, f.venuesExcluded, v);
                    return { ...f, venues: r.active, venuesExcluded: r.excluded };
                  })
                }
                onBulkInclude={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.venues);
                    const nextEx = new Set(f.venuesExcluded);
                    for (const v of values) {
                      nextSel.add(v);
                      nextEx.delete(v);
                    }
                    return { ...f, venues: nextSel, venuesExcluded: nextEx };
                  })
                }
                onBulkClear={(values) =>
                  setFilters((f) => {
                    const nextSel = new Set(f.venues);
                    const nextEx = new Set(f.venuesExcluded);
                    for (const v of values) {
                      nextSel.delete(v);
                      nextEx.delete(v);
                    }
                    return { ...f, venues: nextSel, venuesExcluded: nextEx };
                  })
                }
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

interface ChipRowProps {
  label: string;
  items: readonly string[];
  active: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  /** Cycle the value through off → include → exclude → off. */
  onCycle: (value: string) => void;
  /** Display label / icon for a value; defaults to the tag formatter. */
  labelOf?: (value: string) => string;
  iconOf?: (value: string) => string;
}

function ChipRow({
  label, items, active, excluded, onCycle, labelOf = formatTag, iconOf = tagIcon,
}: ChipRowProps) {
  return (
    <fieldset className="mh-filter-row">
      <legend className="mh-filter-row__label">{label}</legend>
      <div className="mh-filter-row__chips">
        {items.map((item) => {
          const on = active.has(item);
          const ex = excluded.has(item);
          const icon = iconOf(item);
          const cls = ex
            ? 'mh-chip-btn mh-chip-btn--ex'
            : on
              ? 'mh-chip-btn mh-chip-btn--on'
              : 'mh-chip-btn';
          const title = ex
            ? `Excluded — click to clear`
            : on
              ? `Included — click to exclude`
              : `Click to include, click again to exclude`;
          return (
            <button
              key={item}
              type="button"
              className={cls}
              aria-pressed={ex ? 'mixed' : on}
              title={title}
              onClick={() => onCycle(item)}
            >
              {ex && (
                <span className="mh-chip-btn__ex-icon" aria-hidden="true">✕</span>
              )}
              {!ex && icon && (
                <span className="mh-chip-btn__icon" aria-hidden="true">{icon}</span>
              )}
              {labelOf(item)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

interface MultiSelectOption {
  value: string;
  label: string;
  icon?: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  /** Cycle a single value: off → include → exclude → off. */
  onCycle: (value: string) => void;
  /** Bulk-include all of `values` (clearing them from excluded). */
  onBulkInclude: (values: string[]) => void;
  /** Bulk-clear all of `values` from both selected and excluded. */
  onBulkClear: (values: string[]) => void;
}

/**
 * Compact dropdown multi-select rendered as a button + popover with tri-state
 * options. Each row cycles through none → included → excluded → none. The
 * popover has a Select-all / Clear-all toggle scoped to whatever options are
 * currently visible (so a search-narrowed list selects only those rows).
 * Click outside or Esc to close.
 */
function MultiSelect({
  label,
  options,
  selected,
  excluded,
  onCycle,
  onBulkInclude,
  onBulkClear,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Close on outside click / Esc when open. Replaces the old onBlur close path
  // which broke once option rows became <button> (focusable) instead of <label>.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // Only filter the option list once it's reasonably long, to keep small lists snappy.
  const showSearch = options.length > 8;
  const visibleOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const includedCount = options.reduce((n, o) => (selected.has(o.value) ? n + 1 : n), 0);
  const excludedCount = options.reduce((n, o) => (excluded.has(o.value) ? n + 1 : n), 0);
  const summary =
    includedCount === 0 && excludedCount === 0
      ? 'Any'
      : excludedCount === 0
        ? `${includedCount} included`
        : includedCount === 0
          ? `${excludedCount} excluded`
          : `${includedCount} included · ${excludedCount} excluded`;
  const triggerActive = includedCount > 0 || excludedCount > 0;

  const visibleValues = useMemo(() => visibleOptions.map((o) => o.value), [visibleOptions]);
  // "Select all" flips to "Clear all" once every visible option is included.
  // We ignore excluded state when computing this — select-all bulk-clears
  // excludes for the same values to keep the invariant.
  const allVisibleSelected =
    visibleValues.length > 0 && visibleValues.every((v) => selected.has(v));
  const handleSelectAll = () => {
    if (visibleValues.length === 0) return;
    if (allVisibleSelected) onBulkClear(visibleValues);
    else onBulkInclude(visibleValues);
  };

  const clearAll = () => {
    if (includedCount === 0 && excludedCount === 0) return;
    onBulkClear(options.map((o) => o.value));
  };

  return (
    <div ref={wrapperRef} className={`mh-multiselect ${open ? 'mh-multiselect--open' : ''}`}>
      <button
        type="button"
        className={`mh-multiselect__trigger ${triggerActive ? 'mh-multiselect__trigger--active' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mh-multiselect__label">{label}</span>
        <span className="mh-multiselect__summary">
          {summary}
          {includedCount > 0 && (
            <span className="mh-multiselect__badge">{includedCount}</span>
          )}
          {excludedCount > 0 && (
            <span className="mh-multiselect__badge mh-multiselect__badge--ex">
              {excludedCount}
            </span>
          )}
        </span>
        <i className="bi bi-chevron-down ms-1" aria-hidden="true" />
      </button>
      {open && (
        <div className="mh-multiselect__panel" role="listbox" aria-label={label}>
          {showSearch && (
            <input
              type="search"
              className="form-control form-control-sm mh-multiselect__search"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          {visibleValues.length > 0 && (
            <div className="mh-multiselect__toolbar">
              <button
                type="button"
                className="mh-multiselect__bulk"
                onClick={handleSelectAll}
                title={
                  allVisibleSelected
                    ? 'Clear the visible selections'
                    : 'Include every visible option'
                }
              >
                {allVisibleSelected ? 'Clear all' : 'Select all'}
                {query.trim() && (
                  <span className="mh-multiselect__bulk-scope"> (visible)</span>
                )}
              </button>
            </div>
          )}
          <ul className="mh-multiselect__list">
            {visibleOptions.length === 0 && (
              <li className="mh-multiselect__empty">No matches</li>
            )}
            {visibleOptions.map((o) => {
              const on = selected.has(o.value);
              const ex = excluded.has(o.value);
              const cls = ex
                ? 'mh-multiselect__option mh-multiselect__option--ex'
                : on
                  ? 'mh-multiselect__option mh-multiselect__option--on'
                  : 'mh-multiselect__option';
              const stateLabel = ex ? 'Excluded' : on ? 'Included' : 'Off';
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    className={cls}
                    aria-pressed={ex ? 'mixed' : on}
                    title={`${stateLabel} — click to cycle (off → include → exclude)`}
                    onClick={() => onCycle(o.value)}
                  >
                    <span
                      className={`mh-multiselect__state mh-multiselect__state--${ex ? 'ex' : on ? 'on' : 'off'}`}
                      aria-hidden="true"
                    >
                      {ex ? '✕' : on ? '✓' : ''}
                    </span>
                    {o.icon && (
                      <span className="mh-multiselect__icon" aria-hidden="true">{o.icon}</span>
                    )}
                    <span className="mh-multiselect__option-label">{o.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {(includedCount > 0 || excludedCount > 0) && (
            <button type="button" className="mh-multiselect__clear" onClick={clearAll}>
              Clear {includedCount + excludedCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface CustomDateRangeProps {
  filters: FilterState;
  setFilters: (updater: (f: FilterState) => FilterState) => void;
}

/** Two `<input type="date">` controls for the custom date range. */
function CustomDateRange({ filters, setFilters }: CustomDateRangeProps) {
  const fromValue = filters.customFrom ?? '';
  const toValue = filters.customTo ?? '';
  return (
    <div className="d-flex align-items-center gap-1" role="group" aria-label="Custom date range">
      <input
        type="date"
        className="form-control form-control-sm"
        aria-label="From date"
        value={fromValue}
        max={toValue}
        onChange={(e) => setFilters((f) => ({ ...f, customFrom: e.target.value }))}
      />
      <span aria-hidden="true">–</span>
      <input
        type="date"
        className="form-control form-control-sm"
        aria-label="To date"
        value={toValue}
        min={fromValue}
        onChange={(e) => setFilters((f) => ({ ...f, customTo: e.target.value }))}
      />
    </div>
  );
}
interface SavedViewsMenuProps {
  views: SavedView[];
  hasActiveFilters: boolean;
  onApply: (view: SavedView) => void;
  onSave: () => void;
  onDelete: (view: SavedView) => void;
}/** Dropdown menu listing saved views with apply/delete actions and a Save current button. */
function SavedViewsMenu({ views, hasActiveFilters, onApply, onSave, onDelete }: SavedViewsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside / Esc to close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="mh-saved-views" ref={ref}>
      <button
        type="button"
        className="mh-toggle"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        title="Saved views"
      >
        <i className="bi bi-bookmark-star" aria-hidden="true" /> Views
        {views.length > 0 && (
          <span className="mh-toggle__badge" aria-label={`${views.length} saved`}>
            {views.length}
          </span>
        )}
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`} aria-hidden="true" />
      </button>
      {open && (
        <div className="mh-saved-views__panel" role="menu" aria-label="Saved views">
          <button
            type="button"
            className="mh-saved-views__save"
            onClick={() => {
              setOpen(false);
              onSave();
            }}
            disabled={!hasActiveFilters}
            title={hasActiveFilters ? 'Save the current filters as a view' : 'Set some filters first'}
          >
            <i className="bi bi-plus-circle me-1" aria-hidden="true" />
            Save current filters…
          </button>
          {views.length === 0 ? (
            <p className="mh-saved-views__empty">No saved views yet.</p>
          ) : (
            <ul className="mh-saved-views__list">
              {views.map((v) => (
                <li key={v.id} className="mh-saved-views__item">
                  <button
                    type="button"
                    className="mh-saved-views__apply"
                    onClick={() => {
                      setOpen(false);
                      onApply(v);
                    }}
                  >
                    <i className="bi bi-bookmark me-2" aria-hidden="true" />
                    {v.name}
                  </button>
                  <button
                    type="button"
                    className="mh-saved-views__delete"
                    aria-label={`Delete saved view ${v.name}`}
                    title="Delete view"
                    onClick={() => onDelete(v)}
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
